<template>
  <div class="mobile-page">
    <div class="sub-nav">
      <button class="back-btn" @click="back"><el-icon :size="18"><ArrowLeft /></el-icon></button>
      <span class="nav-title">上报详情</span>
      <span class="nav-right"><button class="refresh-btn" @click="load"><el-icon :size="16"><Refresh /></el-icon></button></span>
    </div>

    <div class="mobile-body">
      <div v-if="!gatherEnabled" class="white-card center-box">
        <el-icon :size="40" color="#c0c4cc"><Upload /></el-icon>
        <p class="sub-text">信息收集后台未配置（构建时设置 VITE_API_BASE）</p>
      </div>

      <div v-else-if="showAuth" class="auth-wrap">
        <GatherAuthCard @authed="onAuthed" @skip="showAuth = false" />
      </div>

      <div v-else-if="loading" class="center-box">
        <el-icon class="is-loading" :size="28"><Loading /></el-icon>
        <p class="sub-text">加载中…</p>
      </div>
      <div v-else-if="!record" class="white-card center-box">
        <el-icon :size="40" color="#c0c4cc"><Warning /></el-icon>
        <p class="sub-text">{{ error || '未找到该记录' }}</p>
      </div>

      <template v-else>
        <div class="white-card head-card">
          <div class="hc-top">
            <span class="hc-id mono">{{ record.id }}</span>
            <span class="pill" :class="statusPill(record.status)">{{ record.status }}</span>
          </div>
          <div class="hc-time"><el-icon :size="13"><Calendar /></el-icon> {{ fmt(record.reportTime) }}</div>
          <div class="hc-sync" v-if="record.synced === false">⚠ 该记录尚未同步到本地管理后台</div>
        </div>

        <div class="white-card">
          <div class="info-row"><span class="info-k">上传人</span><span class="info-v">{{ record.reporter || '（未填）' }}</span></div>
          <div class="info-row"><span class="info-k">备注</span><span class="info-v desc">{{ record.description || '（无）' }}</span></div>
          <div class="info-row" v-if="record.department"><span class="info-k">部门</span><span class="info-v">{{ record.department }}</span></div>
          <div class="info-row" v-if="record.hazardType"><span class="info-k">隐患类型</span><span class="info-v">{{ record.hazardType }}</span></div>
          <div class="info-row"><span class="info-k">同步状态</span><span class="info-v" :style="{ color: record.synced ? '#188038' : '#b06000' }">{{ record.synced ? '已同步' : '未同步' }}</span></div>
        </div>

        <!-- 媒体：默认不拉取，点击后加载 -->
        <div class="section-title"><span>现场材料</span><span class="count">{{ mediaCountText }}</span></div>
        <div v-if="mediaTotal > 0 && !mediaLoaded" class="white-card media-bar">
          <div class="load-media-btn" @click="loadMedia">
            <el-icon :size="18"><CameraFilled /></el-icon>
            <span>加载照片 / 视频（{{ mediaTotal }} 个文件）</span>
          </div>
          <p class="tiny-tip">为节省流量，照片与视频默认不下载，按需加载。</p>
        </div>

        <div v-if="mediaLoaded && allMedia.length === 0" class="white-card center-box" style="padding:24px">
          <p class="sub-text" style="color:#9aa0a6">该记录没有照片或视频</p>
        </div>
        <div v-else-if="mediaLoaded" class="media-grid">
          <div v-for="(m, i) in allMedia" :key="i" class="media-cell" @click="openViewer(i)">
            <img v-if="m.type === 'image'" :src="m.url" loading="lazy" />
            <div v-else class="video-cell">
              <el-icon :size="24"><VideoPlay /></el-icon>
            </div>
            <span class="cell-tag">{{ m.type === 'image' ? '图' : '影' }}{{ m.no }}</span>
          </div>
        </div>
        <div v-else-if="mediaTotal === 0" class="white-card center-box" style="padding:24px">
          <p class="sub-text" style="color:#9aa0a6">该记录没有照片或视频</p>
        </div>

        <!-- 状态操作 -->
        <div class="section-title"><span>处理状态</span></div>
        <div class="status-actions">
          <div
            v-for="s in ['未处理', '处理中', '已处理']" :key="s"
            class="status-option" :class="{ on: record.status === s }"
            @click="changeStatus(s)"
          >
            <span class="dot" :class="'dot-' + dotKey(s)"></span>
            {{ s }}
          </div>
        </div>
      </template>
    </div>

    <!-- 全屏查看器 -->
    <teleport to="body">
      <transition name="viewer">
        <div v-if="viewerVisible" class="viewer" @click.self="viewerVisible = false">
          <div class="viewer-top">
            <button @click="viewerVisible = false"><el-icon :size="22"><Close /></el-icon></button>
            <span class="viewer-count">{{ viewerIndex + 1 }} / {{ allMedia.length }}</span>
            <button v-if="currentMedia.type === 'image'" @click="downloadCurrent"><el-icon :size="20"><Download /></el-icon></button>
            <span v-else class="viewer-count"></span>
          </div>
          <div class="viewer-body">
            <img v-if="currentMedia.type === 'image'" :src="currentMedia.url" />
            <video v-else :src="currentMedia.url" controls autoplay playsinline></video>
          </div>
          <div class="viewer-btns">
            <button :disabled="viewerIndex === 0" @click="viewerIndex--"><el-icon><ArrowLeft /></el-icon></button>
            <button :disabled="viewerIndex >= allMedia.length - 1" @click="viewerIndex++"><el-icon><ArrowRight /></el-icon></button>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { fetchGatherRecords, updateGatherStatus, getGatherImageUrl, getGatherVideoUrl, getGatherDownloadUrl, gatherEnabled, isAuthError } from '../api'
import GatherAuthCard from '../components/GatherAuthCard.vue'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()
function back() { router.back() }

const record = ref(null)
const loading = ref(true)
const error = ref('')
const viewerVisible = ref(false)
const viewerIndex = ref(0)
const mediaLoaded = ref(false)
const showAuth = ref(false)

function onAuthed() {
  showAuth.value = false
  load()
}

const mediaTotal = computed(() => {
  const r = record.value
  if (!r) return 0
  return ((r.images || []).length || 0) + ((r.videos || []).length || 0)
})

function loadMedia() {
  if (mediaLoaded.value) return
  mediaLoaded.value = true
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const id = route.params.id
    let found = null
    for (let p = 1; p <= 10; p++) {
      const data = await fetchGatherRecords({ page: p, pageSize: 100 })
      const list = data.data || []
      found = list.find(r => r.id === id) || null
      if (found) break
      if (!data.total || p * 100 >= data.total) break
    }
    record.value = found
    if (!found) error.value = '未找到该记录（' + id + '）'
  } catch (e) {
    if (isAuthError(e)) {
      showAuth.value = true
      error.value = ''
    } else {
      error.value = e.message || '加载失败'
    }
  } finally {
    loading.value = false
  }
}

const allMedia = computed(() => {
  if (!record.value || !mediaLoaded.value) return []
  const list = []
  const imgs = record.value.images || []
  imgs.forEach((_, i) => list.push({ type: 'image', no: i + 1, url: getGatherImageUrl(record.value.id, i) }))
  const vids = record.value.videos || []
  vids.forEach((_, i) => list.push({ type: 'video', no: i + 1, url: getGatherVideoUrl(record.value.id, i) }))
  return list
})
const currentMedia = computed(() => allMedia.value[viewerIndex.value] || {})
const mediaCountText = computed(() => `${(record.value?.images || []).length} 图 / ${(record.value?.videos || []).length} 视频`)

function openViewer(i) {
  viewerIndex.value = i
  viewerVisible.value = true
}

function downloadCurrent() {
  if (!record.value || currentMedia.value.type !== 'image') return
  const url = getGatherDownloadUrl(record.value.id, viewerIndex.value)
  const a = document.createElement('a')
  a.href = url
  a.download = `${record.value.id}_${viewerIndex.value + 1}.jpg`
  a.click()
}

function fmt(t) { return t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '' }
function statusPill(s) {
  if (s === '已处理') return 'pill-green'
  if (s === '处理中') return 'pill-orange'
  return 'pill-red'
}
function dotKey(s) {
  if (s === '未处理') return 'pending'
  if (s === '处理中') return 'processing'
  return 'done'
}

async function changeStatus(status) {
  if (!record.value || record.value.status === status) return
  try {
    await ElMessageBox.confirm(`确认将状态修改为「${status}」？`, '修改状态', {
      confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning'
    })
    await updateGatherStatus(record.value.id, status)
    record.value.status = status
    ElMessage.success('状态已更新')
  } catch (e) {
    if (e !== 'cancel') ElMessage.error('更新失败：' + (e.message || ''))
  }
}

onMounted(load)
</script>

<style scoped>
.head-card { margin-bottom: 10px; }
.hc-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 7px; }
.hc-id { font-size: 17px; font-weight: 800; color: #202124; }
.hc-time { display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: #9aa0a6; }
.hc-sync { font-size: 12px; color: #b06000; margin-top: 6px; }

.info-row { display: flex; gap: 10px; padding: 7px 0; font-size: 13.5px; border-bottom: 1px solid #f6f7f9; }
.info-row:last-child { border-bottom: none; }
.info-k { width: 66px; flex-shrink: 0; color: #9aa0a6; }
.info-v { color: #3c4043; word-break: break-all; line-height: 1.5; }

.media-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.media-cell { position: relative; aspect-ratio: 1; border-radius: 10px; overflow: hidden; background: #f1f3f4; cursor: pointer; }
.media-cell img { width: 100%; height: 100%; object-fit: cover; }
.video-cell { width: 100%; height: 100%; background: #303133; color: #fff; display: flex; align-items: center; justify-content: center; }
.cell-tag { position: absolute; bottom: 3px; right: 4px; font-size: 10px; color: #fff; background: rgba(0,0,0,.6); padding: 0 5px; border-radius: 4px; }

.count { font-size: 12px; font-weight: 400; color: #9aa0a6; }

.load-media-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  background: #e8f0fe; color: #1a73e8; border: 1.5px dashed #8ab4f8; border-radius: 12px;
  padding: 13px; font-size: 14px; font-weight: 600; cursor: pointer;
}
.load-media-btn:active { background: #d2e3fc; }
.tiny-tip { font-size: 11px; color: #b0b3bb; text-align: center; margin-top: 6px; }

.status-actions { display: flex; gap: 8px; }
.status-option {
  flex: 1; text-align: center; padding: 12px 4px; border-radius: 12px; font-size: 13.5px;
  color: #5f6368; border: 1.5px solid #e4e7ed; background: #fafbfc; cursor: pointer;
  display: flex; flex-direction: column; align-items: center; gap: 5px; transition: all .15s;
}
.status-option:active { transform: scale(.96); }
.status-option.on { border-color: #1a73e8; background: #e8f0fe; color: #1a73e8; font-weight: 700; }
.dot { width: 12px; height: 12px; border-radius: 50%; }
.dot-pending { background: #e6a23c; }
.dot-processing { background: #1a73e8; }
.dot-done { background: #34a853; }
</style>
